/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */
import { sidebarPaperSX } from '@/components/blocks/SideBar/styles/paper';
import { CatalogEntryList } from '@/components/content/CatalogEntryList';
import { ID } from '@/data/types/Basic';
import { Accordion, Grid, Skeleton, Typography } from '@mui/material';
import { FC, Fragment, PropsWithChildren } from 'react';
import { ShowByCondition } from '@/components/blocks/ShowByCondition';

type Props = {
	title: string;
	id?: ID;
	href?: string;
	scrollable?: boolean;
	isMobile: boolean;
	total?: number;
	loading?: boolean;
	toggleOpen: () => void;
};

export const SidebarContent: FC<PropsWithChildren<Props>> = ({
	title,
	total,
	isMobile,
	id,
	scrollable = false,
	children,
	loading = false,
}) => (
	<Grid sx={sidebarPaperSX({ isMobile, scrollable })}>
		<ShowByCondition condition={!isMobile}>
			{loading ? (
				<Skeleton
					variant="text"
					sx={{
						marginBottom: '8px',
						height: { xs: '24px', md: '36px' },
					}}
				/>
			) : (
				<Typography
					component={'h3'}
					sx={{
						fontSize: 'var(--font-size-body)',
						fontWeight: '800',
						display: 'flex',
						alignItems: 'center',
						gap: '2px',
						marginBottom: '8px',
						font: 'normal normal 800 15px/20px Open Sans',
						letterSpacing: '0px',
						color: '#2C2C2C',
						'-webkit-font-smoothing': 'antialiased',
						'-moz-osx-font-smoothing': 'grayscale',
					}}
				>
					{title}
					<Typography variant={'body1'} sx={{ color: '#929292' }}>
						{`(${total || 0})`}
					</Typography>
				</Typography>
			)}
		</ShowByCondition>

		<ShowByCondition condition={isMobile}>
			<CatalogEntryList id={id!} isFilter />
		</ShowByCondition>
		<Accordion
			sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}
			expanded={true}
			square={true}
			id="sidebar-content"
			data-testid="sidebar-content"
		>
			{loading ? (
				<Skeleton variant="rectangular" sx={{ height: { xs: '35vh', md: '90vh' } }} />
			) : (
				<Fragment>{children}</Fragment>
			)}
		</Accordion>
	</Grid>
);
